import React from 'react';
import { styled } from '@mui/system';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Dialog,
    DialogContent,
    Button,
    TableContainer,
    Paper,
    Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import {
    calculateChildEnhancementDepreciation,
    computeAdjustedGainBeforeTax,
} from '../../../../Utils/capitalGainCalculations';

const BoundsWrapper = styled('div')({
    position: 'fixed',
    inset: 0,
    zIndex: 210,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.35)',
});

const sectionCardSx = {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '10px 12px',
    backgroundColor: '#fafafa',
    height: '100%',
    boxSizing: 'border-box',
};

const HISULIT_PURPLE = '#7b1fa2';

const formatDateHe = (d) =>
    !d ? '—' : new Date(d).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });

const formatGainAmount = (value) => {
    const n = Number(value) || 0;
    if (n < 0) return `(${beautifyNumber(Math.abs(n), 0)})`;
    return beautifyNumber(n, 0);
};

const formatEnhancementAssetLabel = (child) => {
    const num = child?.assetNumber ?? child?.AssetNumber;
    const name = child?.assetName ?? child?.AssetName ?? child?.name ?? '—';
    return num != null && num !== '' ? `${num} - ${name}` : name;
};

const getEnhancementAssetId = (asset) =>
    asset?.id ??
    asset?.Id ??
    asset?.enhancementAssetId ??
    asset?.EnhancementAssetId;

const enhancementAssetTagSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    height: 22,
    pl: 0.75,
    pr: 0.125,
    borderRadius: '4px',
    border: '1px solid #304FFF',
    bgcolor: '#eef1ff',
    maxWidth: '100%',
    minWidth: 0,
};

const EMPTY_REPLACEMENT_DATA = {
    selectedAssets: [],
    realReplacementAmount: 0,
    inflationaryReplacementAmount: 0,
};

const isAssetSold = (a) => {
    if (!a) return false;
    return !!(
        a.sellDate ||
        a.SellDate ||
        (a.sellAmount != null && a.sellAmount !== '') ||
        (a.SellAmount != null && a.SellAmount !== '') ||
        a.saleIncomeId ||
        a.SaleIncomeId
    );
};

const getAssetIdValue = (a) => a?.id ?? a?.Id ?? null;

const sanitizeFileNamePart = (value) =>
    String(value ?? '')
        .trim()
        .replace(/[/\\?%*:|"<>]/g, '_')
        .replace(/\s+/g, '_');

function InfoRowGroup({ items, direction }) {
    const isRtl = direction === 'rtl';
    const columnCount = items.length;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: `repeat(${columnCount}, 1fr)`,
                },
                gap: { xs: 0.75, sm: 1 },
                borderBottom: '1px solid #e0e0e0',
                py: 0.50,
                minHeight: 24,
                direction,
            }}
        >
            {items.map(({ label, value }, index) => (
                <Box
                    key={index}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.50,
                        minWidth: 0,
                    }}
                >
                    <Typography
                        component="div"
                        sx={{
                            fontWeight: 600,
                            color: '#333',
                            fontSize: 14,
                            lineHeight: 1.25,
                            textAlign: isRtl ? 'left' : 'right',
                        }}
                    >
                        {label}
                    </Typography>
                    <Box
                        sx={{
                            fontWeight: 500,
                            fontSize: 13,
                            lineHeight: 1.25,
                            textAlign: isRtl ? 'left' : 'right',
                            wordBreak: 'break-word',
                        }}
                    >
                        {value}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

function SectionCard({ title, children, direction, titleColor }) {
    const isRtl = direction === 'rtl';

    return (
        <Box sx={{ ...sectionCardSx, direction }}>
            <Typography
                sx={{
                    fontWeight: 700,
                    mb: 0.75,
                    color: titleColor || '#1a237e',
                    fontSize: 15,
                    textAlign: isRtl ? 'left' : 'right',
                }}
            >
                {title}
            </Typography>
            {children}
        </Box>
    );
}

function EnhancementAssetTag({ child, onRemove, direction }) {
    return (
        <Box sx={{ ...enhancementAssetTagSx, direction }}>
            <IconButton
                size="small"
                aria-label="remove"
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove?.(child);
                }}
                sx={{
                    p: 0,
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    color: '#5c6bc0',
                    '&:hover': {
                        color: '#d32f2f',
                        bgcolor: 'rgba(211, 47, 47, 0.08)',
                    },
                }}
            >
                <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
            <Typography
                component="span"
                sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#304FFF',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {formatEnhancementAssetLabel(child)}
            </Typography>

        </Box>
    );
}

function EnhancementsBalanceRow({
    direction,
    label,
    value,
    selectedAssets = [],
    onRemoveAsset,
    onOpenPicker,
    disabled,
}) {
    const isRtl = direction === 'rtl';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.35,
                borderBottom: '1px solid #e0e0e0',
                py: 0.5,
                minHeight: 24,
                direction,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    justifyContent: isRtl ? 'flex-end' : 'flex-start',
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        fontWeight: 600,
                        color: '#333',
                        fontSize: 14,
                        lineHeight: 1.25,
                    }}
                >
                    {label}
                </Typography>
                <IconButton
                    size="small"
                    onClick={onOpenPicker}
                    disabled={disabled}
                    sx={{
                        p: 0.25,
                        border: '1px solid #bdbdbd',
                        borderRadius: '4px',
                        width: 24,
                        height: 24,
                    }}
                >
                    <MoreHorizIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {(selectedAssets.length > 0 || value) && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: isRtl ? 'flex-end' : 'flex-start',
                        gap: 0.5,
                        width: '100%',
                    }}
                >
                    {selectedAssets.map((child) => (
                        <EnhancementAssetTag
                            key={child.id ?? child.Id}
                            child={child}
                            direction={direction}
                            onRemove={onRemoveAsset}
                        />
                    ))}

                    <Typography
                        component="div"
                        sx={{
                            fontWeight: 600,
                            fontSize: 13,
                            lineHeight: 1.25,
                            textAlign: isRtl ? 'left' : 'right',
                            direction: 'ltr',
                            flexShrink: 0,
                        }}
                    >
                        {value}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

function EnhancementsPickerDialog({
    open,
    onClose,
    direction,
    t,
    assets,
    selectedIds,
    onToggle,
    onSave,
    savingEnhancements = false,
    saleDate,
    parentDepreciationPercentage,
}) {
    const isRtl = direction === 'rtl';

    const getChildEnhancementValues = (child) => {
        const backendRemaining =
            child.remainingOriginalCostAtSale ??
            child.RemainingOriginalCostAtSale ??
            child.remainingOriginalCost ??
            child.RemainingOriginalCost;
        const backendDepreciation =
            child.accumulatedDepreciationAtSale ?? child.AccumulatedDepreciationAtSale;

        if (backendRemaining != null && backendRemaining !== '') {
            const childRemainingOriginalCost = Number(backendRemaining) || 0;
            const childAccumulatedDepreciation =
                backendDepreciation != null && backendDepreciation !== ''
                    ? Number(backendDepreciation) || 0
                    : Math.max(
                          0,
                          Number(child.originalPrice ?? child.OriginalPrice ?? 0) -
                              childRemainingOriginalCost
                      );
            return { childAccumulatedDepreciation, childRemainingOriginalCost };
        }

        return calculateChildEnhancementDepreciation({
            childOriginalPrice: child.originalPrice ?? child.OriginalPrice,
            childPurchaseDate: child.purchaseDate ?? child.PurchaseDate,
            childActivationDate: child.activationDate ?? child.ActivationDate,
            saleDate,
            depreciationPercentage: child.depreciationPercentage ?? child.DepreciationPercentage,
            parentDepreciationPercentage,
        });
    };

    const selectedAssets = assets.filter((child) => selectedIds.has(child.id ?? child.Id));

    const selectedTotal = selectedAssets.reduce((sum, child) => {
        const { childRemainingOriginalCost } = getChildEnhancementValues(child);
        return sum + childRemainingOriginalCost;
    }, 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            dir={direction}
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    border: '1px solid #304FFF',
                    boxShadow: '0 8px 32px rgba(48, 79, 255, 0.18)',
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    background: 'linear-gradient(135deg, #304FFF 0%, #5c6bc0 100%)',
                    color: '#fff',
                    direction,
                }}
            >
                <IconButton size="small" onClick={onClose} sx={{ color: '#fff', p: 0.5 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 700, flex: 1, textAlign: isRtl ? 'left' : 'right' }}>
                    {t('EnhancementsPickerTitle') || 'יתרת השבחות — בחירת נכסים'}
                </Typography>

            </Box>

            <DialogContent sx={{ direction, p: 0, bgcolor: '#fafbff' }}>
                {assets.length === 0 ? (
                    <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                        <Typography sx={{ color: '#666', fontSize: 14 }}>
                            {t('NoEnhancementAssets') || 'אין נכסי השבחה קשורים'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                                px: 2,
                                py: 1,
                                borderBottom: '1px solid #e3e8ff',
                                bgcolor: '#fff',
                                direction,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: '#555',
                                    fontWeight: 500,
                                    flexShrink: 0,
                                }}
                            >
                                {t('EnhancementsBalance') || 'יתרת השבחות'}
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                    flex: 1,
                                    minWidth: 0,
                                    justifyContent: isRtl ? 'flex-end' : 'flex-start',
                                }}
                            >
                                {selectedAssets.length === 0 ? (
                                    <Typography sx={{ fontSize: 12, color: '#999' }}>
                                        {t('NoAssetsSelected') || 'לא נבחרו נכסים'}
                                    </Typography>
                                ) : (
                                    selectedAssets.map((child) => (
                                        <Chip
                                            key={child.id ?? child.Id}
                                            label={formatEnhancementAssetLabel(child)}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 500,
                                                borderColor: '#304FFF',
                                                color: '#304FFF',
                                                bgcolor: '#f5f7ff',
                                                maxWidth: '100%',
                                                '& .MuiChip-label': {
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                },
                                            }}
                                        />
                                    ))
                                )}
                            </Box>

                            <Chip
                                label={beautifyNumber(selectedTotal, 0)}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 13,
                                    bgcolor: selectedTotal > 0 ? '#e8eaf6' : '#f5f5f5',
                                    color: selectedTotal > 0 ? '#304FFF' : '#888',
                                    direction: 'ltr',
                                    flexShrink: 0,
                                }}
                            />
                        </Box>

                        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 320, bgcolor: 'transparent' }}>
                            <Table
                                size="small"
                                stickyHeader
                                sx={{
                                    direction,
                                    '& .MuiTableCell-root': {
                                        borderColor: '#e8eaf6',
                                        fontSize: 13,
                                    },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontWeight: 700,
                                                bgcolor: '#f0f3ff',
                                                borderBottom: '2px solid #304FFF',
                                                color: HISULIT_PURPLE,
                                            }}
                                        >
                                            {t('ReductionBalance') || 'יתרה להפחתה'}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ fontWeight: 700, bgcolor: '#f0f3ff', borderBottom: '2px solid #304FFF' }}
                                        >
                                            {t('CalculatedDepreciation') || 'פחת מחושב'}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ fontWeight: 700, bgcolor: '#f0f3ff', borderBottom: '2px solid #304FFF' }}
                                        >
                                            {t('OriginalPrice')}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ fontWeight: 700, bgcolor: '#f0f3ff', borderBottom: '2px solid #304FFF' }}
                                        >
                                            {t('PurchaseDate')}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ fontWeight: 700, bgcolor: '#f0f3ff', borderBottom: '2px solid #304FFF' }}
                                        >
                                            {t('AssetName')}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ fontWeight: 700, bgcolor: '#f0f3ff', borderBottom: '2px solid #304FFF' }}
                                        >
                                            {t('AssetNumber')}
                                        </TableCell>
                                        <TableCell
                                            padding="checkbox"
                                            sx={{
                                                width: 44,
                                                bgcolor: '#f0f3ff',
                                                borderBottom: '2px solid #304FFF',
                                            }}
                                        />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {assets.map((child, index) => {
                                        const childId = child.id ?? child.Id;
                                        const isSelected = selectedIds.has(childId);
                                        const originalPrice =
                                            child.originalPrice ?? child.OriginalPrice ?? 0;
                                        const { childAccumulatedDepreciation, childRemainingOriginalCost } =
                                            getChildEnhancementValues(child);

                                        return (
                                            <TableRow
                                                key={childId}
                                                hover
                                                onClick={() => onToggle(child)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    bgcolor: isSelected ? '#eef1ff' : index % 2 === 0 ? '#fff' : '#fafbff',
                                                    transition: 'background-color 0.15s ease',
                                                    ...(isSelected && {
                                                        boxShadow: isRtl
                                                            ? 'inset -4px 0 0 #304FFF'
                                                            : 'inset 4px 0 0 #304FFF',
                                                    }),
                                                    '&:hover': {
                                                        bgcolor: isSelected ? '#e3e8ff' : '#f5f7ff',
                                                    },
                                                }}
                                            >
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: isSelected ? HISULIT_PURPLE : '#333',
                                                        direction: 'ltr',
                                                    }}
                                                >
                                                    {beautifyNumber(childRemainingOriginalCost, 0)}
                                                </TableCell>
                                                <TableCell align="center" sx={{ direction: 'ltr' }}>
                                                    {beautifyNumber(childAccumulatedDepreciation, 0)}
                                                </TableCell>
                                                <TableCell align="center" sx={{ direction: 'ltr' }}>
                                                    {beautifyNumber(originalPrice, 0)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {formatDateHe(child.purchaseDate ?? child.PurchaseDate)}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                                    {child.assetName ?? child.AssetName ?? child.name ?? '—'}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                                    {child.assetNumber ?? child.AssetNumber ?? '—'}
                                                </TableCell>
                                                <TableCell padding="checkbox" align="center">
                                                    {isSelected ? (
                                                        <CheckCircleIcon sx={{ fontSize: 20, color: '#304FFF' }} />
                                                    ) : (
                                                        <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: '#bdbdbd' }} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: isRtl ? 'flex-start' : 'flex-end',
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    borderTop: '1px solid #e3e8ff',
                    bgcolor: '#fff',
                    direction,
                }}
            >
                <Button
                    onClick={onClose}
                    size="small"
                    disabled={savingEnhancements}
                    sx={{ textTransform: 'none' }}
                >
                    {t('Close') || 'סגור'}
                </Button>
                <Button
                    onClick={onSave}
                    variant="contained"
                    size="small"
                    disabled={savingEnhancements}
                    sx={{
                        bgcolor: '#304FFF',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        '&:hover': { bgcolor: '#253dcc' },
                    }}
                >
                    {savingEnhancements ? (
                        <CircularProgress size={18} sx={{ color: '#fff' }} />
                    ) : (
                        t('SaveEnhancementsSelection') || 'שמור בחירה'
                    )}
                </Button>
            </Box>
        </Dialog>
    );
}

function CapitalGainTaxTable({
    direction,
    t,
    gains,
    tax,
    replacementSummary,
    adjustedGains,
    inflationaryTaxRate,
    onInflationaryTaxRateChange,
    realFrom2003TaxRate,
    onRealFrom2003TaxRateChange,
    realUntil2002TaxRate,
    onRealUntil2002TaxRateChange,
    advancePaid,
    setAdvancePaid,
    tableInputSx,
    isRtl,
    maxTaxConfig,
}) {
    const tableDirection = direction === 'rtl' ? 'ltr' : 'rtl';
    const tableIsRtl = tableDirection === 'rtl';
    const maxTaxLabel = `${t('MaxTaxSection91') || 'המס המירבי לפי סעיף'} ${maxTaxConfig.section}: ${maxTaxConfig.rate}%`;

    const columns = [
        { key: 'total', label: t('CapitalGainTotalColumn') || 'רווח ההון' },
        { key: 'inflationary', label: t('InflationaryTaxableColumn') || "סכום אינפ' חייב" },
        { key: 'realFrom2003', label: t('RealGainFrom2003Column') || 'ר. ריאלי מ-2003' },
        { key: 'realUntil2002', label: t('RealGainUntil2002Column') || 'ר. ריאלי עד 2002' },
        { key: 'loss', label: t('RealLossColumn') || 'הפסד ריאלי' },
        { key: 'exempt', label: t('ExemptGainColumn') || 'רווח פטור' },
    ];

    const labelCellSx = {
        backgroundColor: '#eceff1',
        fontWeight: 600,
        fontSize: 13,
        lineHeight: 1.25,
        textAlign: tableIsRtl ? 'right' : 'left',
        py: 0.5,
        px: 1,
        whiteSpace: 'nowrap',
        minWidth: 130,
    };

    const headerCellSx = {
        fontWeight: 700,
        fontSize: 13,
        lineHeight: 1.25,
        textAlign: 'center',
        py: 0.75,
        px: 0.5,
        borderBottom: `2px solid ${HISULIT_PURPLE}`,
        whiteSpace: 'nowrap',
    };

    const valueCellSx = {
        textAlign: 'center',
        fontSize: 13,
        lineHeight: 1.25,
        py: 0.5,
        px: 0.5,
        fontWeight: 500,
        direction: 'ltr',
    };

    const totalRowSx = {
        '& td': {
            borderTop: '3px double #333',
            fontWeight: 800,
            fontSize: 14,
        },
    };

    const emptyCell = () => <TableCell sx={valueCellSx} />;

    const formatCellAmount = (value) => {
        const n = Number(value) || 0;
        if (n === 0) return beautifyNumber(0, 0);
        return formatGainAmount(n);
    };

    const gainRowByKey = {
        total: formatGainAmount(gains.totalCapitalGain),
        inflationary: formatCellAmount(gains.inflationaryTaxableGain),
        realFrom2003: formatCellAmount(gains.realFrom2003),
        realUntil2002: formatCellAmount(gains.realUntil2002),
        loss: gains.realLoss > 0 ? formatGainAmount(gains.realLoss) : formatCellAmount(0),
        exempt:
            gains.inflationaryExemptGain > 0
                ? formatGainAmount(gains.inflationaryExemptGain)
                : formatCellAmount(0),
    };

    const taxAmountRowByKey = {
        total: formatCellAmount(tax.totalTax),
        inflationary: formatCellAmount(tax.taxOnInflationary),
        realFrom2003: formatCellAmount(tax.taxOnRealFrom2003),
        realUntil2002: formatCellAmount(tax.taxOnRealUntil2002),
        loss: formatCellAmount(0),
        exempt: formatCellAmount(0),
    };

    const taxPercentCellByKey = {
        inflationary: (
            <TextField
                size="small"
                type="number"
                value={inflationaryTaxRate}
                onChange={onInflationaryTaxRateChange}
                inputProps={{ min: 0, max: maxTaxConfig.rate }}
                sx={tableInputSx(72)}
            />
        ),
        realFrom2003: (
            <TextField
                size="small"
                type="number"
                value={realFrom2003TaxRate}
                onChange={onRealFrom2003TaxRateChange}
                inputProps={{ min: 0, max: maxTaxConfig.rate }}
                sx={tableInputSx(72)}
            />
        ),
        realUntil2002: (
            <TextField
                size="small"
                type="number"
                value={realUntil2002TaxRate}
                onChange={onRealUntil2002TaxRateChange}
                disabled={!(Number(gains.realUntil2002) > 0)}
                inputProps={{ min: 0, max: maxTaxConfig.rate }}
                sx={tableInputSx(72)}
            />
        ),
    };

    return (
        <Box sx={{  direction: tableDirection }}>
            <Table
                size="small"
                sx={{
                    minWidth: 780,
                    direction: tableDirection,
                    borderCollapse: 'collapse',
                    backgroundColor: '#fff',
                    '& .MuiTableCell-root': {
                        border: '1px solid #bdbdbd',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ ...labelCellSx, borderBottom: `2px solid ${HISULIT_PURPLE}` }} />
                        {columns.map((col) => (
                            <TableCell key={col.key} sx={headerCellSx}>
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell sx={labelCellSx}>
                            {t('TotalCapitalGain') || 'רווח (הפסד) הון'} :
                        </TableCell>
                        {columns.map((col) => (
                            <TableCell key={col.key} sx={valueCellSx}>
                                {gainRowByKey[col.key]}
                            </TableCell>
                        ))}
                    </TableRow>

                    <TableRow>
                        {/* Hisulit-style Section 96 replacement row.
                            Shows amounts transferred from the replacement modal and reduces taxable gains. */}
                        <TableCell sx={labelCellSx}>
                            {t('ReplacementWithNewAsset') || 'שחלוף (עם נכס חדש)'} :
                        </TableCell>
                        <TableCell sx={valueCellSx}>{formatCellAmount(0)}</TableCell>
                        <TableCell sx={valueCellSx}>
                            {formatCellAmount(replacementSummary?.inflationaryReplacementAmount)}
                        </TableCell>
                        <TableCell sx={valueCellSx}>
                            {formatCellAmount(replacementSummary?.replacementFrom2003)}
                        </TableCell>
                        <TableCell sx={valueCellSx}>
                            {formatCellAmount(replacementSummary?.replacementUntil2002)}
                        </TableCell>
                        <TableCell sx={valueCellSx}>{formatCellAmount(0)}</TableCell>
                        <TableCell sx={valueCellSx}>{formatCellAmount(0)}</TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell sx={{ ...labelCellSx, fontWeight: 700 }}>
                            {t('GainBeforeTax') || 'רווח (הפסד) לפני מס'} :
                        </TableCell>
                        <TableCell sx={{ ...valueCellSx, fontWeight: 700 }}>
                            {formatGainAmount(
                                computeAdjustedGainBeforeTax(gains, adjustedGains)
                            )}
                        </TableCell>
                        <TableCell sx={{ ...valueCellSx, fontWeight: 700 }}>
                            {formatCellAmount(adjustedGains?.adjustedInflationaryTaxableGain)}
                        </TableCell>
                        <TableCell sx={{ ...valueCellSx, fontWeight: 700 }}>
                            {formatCellAmount(adjustedGains?.adjustedRealFrom2003)}
                        </TableCell>
                        <TableCell sx={{ ...valueCellSx, fontWeight: 700 }}>
                            {formatCellAmount(adjustedGains?.adjustedRealUntil2002)}
                        </TableCell>
                        <TableCell sx={{ ...valueCellSx, fontWeight: 700 }}>
                            {gains.realLoss > 0
                                ? formatGainAmount(gains.realLoss)
                                : formatCellAmount(0)}
                        </TableCell>
                        <TableCell sx={{ ...valueCellSx, fontWeight: 700 }}>
                            {gains.inflationaryExemptGain > 0
                                ? formatGainAmount(gains.inflationaryExemptGain)
                                : formatCellAmount(0)}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell sx={labelCellSx}>{t('TaxPercent') || 'אחוז מס'} :</TableCell>
                        {columns.map((col) => (
                            <TableCell key={col.key} sx={valueCellSx}>
                                {taxPercentCellByKey[col.key] || null}
                            </TableCell>
                        ))}
                    </TableRow>

                    <TableRow>
                        <TableCell sx={labelCellSx}>
                            {t('TaxAmountDue') || 'סכום המס לתשלום'} :
                        </TableCell>
                        {columns.map((col) => (
                            <TableCell
                                key={col.key}
                                sx={{
                                    ...valueCellSx,
                                    fontWeight: col.key === 'total' ? 700 : 500,
                                }}
                            >
                                {taxAmountRowByKey[col.key]}
                            </TableCell>
                        ))}
                    </TableRow>

                    <TableRow>
                        <TableCell sx={labelCellSx}>{t('AdvancePaid') || 'המקדמה ששולמה'} :</TableCell>
                        <TableCell sx={valueCellSx}>
                            <TextField
                                size="small"
                                type="number"
                                value={advancePaid}
                                onChange={(e) => setAdvancePaid(e.target.value)}
                                sx={tableInputSx(100)}
                            />
                        </TableCell>
                        {columns.slice(1).map((col) => (
                            <React.Fragment key={col.key}>{emptyCell()}</React.Fragment>
                        ))}
                    </TableRow>

                    <TableRow sx={totalRowSx}>
                        <TableCell sx={{ ...labelCellSx, fontWeight: 800 }}>
                            {t('BalanceDue') || 'יתרה לתשלום'} :
                        </TableCell>
                        <TableCell sx={{ ...valueCellSx, fontWeight: 800 }}>
                            {formatGainAmount(tax.balanceDue)}
                        </TableCell>
                        {columns.slice(1).map((col) => (
                            <React.Fragment key={col.key}>{emptyCell()}</React.Fragment>
                        ))}
                    </TableRow>
                </TableBody>
            </Table>

            <Typography
                variant="caption"
                sx={{
                    color: HISULIT_PURPLE,
                    mt: 1,
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 700,
                    textAlign: isRtl ? 'left' : 'right',
                }}
            >
                {maxTaxLabel}
            </Typography>
        </Box>
    );
}

function CapitalGainAssetDetailsSection({
    direction,
    t,
    basis,
    gains,
    enhancementsBalance,
    enhancementsAccumulatedDepreciation,
    selectedEnhancementAssets,
    onRemoveEnhancementAsset,
    onOpenEnhancementsPicker,
    loadingEnhancementCandidates,
    saleExpenses,
    onSaleExpensesChange,
    salePriceInput,
    onSalePriceInputChange,
    inputFieldSx,
}) {
    return (
        <Box>
            <InfoRowGroup
                direction={direction}
                items={[
                    { label: t('SellDate'), value: formatDateHe(basis.sellDate) },
                    { label: t('PurchaseDate'), value: formatDateHe(basis.purchaseDate) },
                    { label: t('AssetName'), value: basis.name },


                ]}
            />
            <InfoRowGroup
                direction={direction}
                items={[
                    {
                        label: t('AccumulatedDepreciation') || 'פחת שנצבר',
                        value: beautifyNumber(basis.accumulatedDepreciationAtSale, 0),
                    },
                    {
                        label: t('RemainingOriginalCost') || 'יתרת מחיר מקורי',
                        value: beautifyNumber(gains?.remainingOriginalCost ?? basis.remainingOriginalCost, 0),
                    },
                    { label: t('OriginalPrice'), value: beautifyNumber(basis.originalPrice, 0) },

                ]}
            />
            <EnhancementsBalanceRow
                direction={direction}
                label={t('EnhancementsBalance') || 'יתרת השבחות'}
                value={beautifyNumber(enhancementsBalance, 0)}
                selectedAssets={selectedEnhancementAssets}
                onRemoveAsset={onRemoveEnhancementAsset}
                onOpenPicker={onOpenEnhancementsPicker}
                disabled={loadingEnhancementCandidates}
            />
            {selectedEnhancementAssets.length > 0 && (
                <InfoRowGroup
                    direction={direction}
                    items={[
                        {
                            label: t('EnhancementsDepreciation') || 'פחת השבחות',
                            value: beautifyNumber(enhancementsAccumulatedDepreciation, 0),
                        },
                        {
                            label: t('TotalAccumulatedDepreciation') || 'פחת שנצבר כולל',
                            value: beautifyNumber(
                                gains?.totalAccumulatedDepreciation ??
                                    Number(basis.accumulatedDepreciationAtSale || 0) +
                                        enhancementsAccumulatedDepreciation,
                                0
                            ),
                        },
                    ]}
                />
            )}
            <InfoRowGroup
                direction={direction}
                items={[
                    {
                        label: t('SaleExpenses') || 'הוצאות מכירה',
                        value: (
                            <TextField
                                size="small"
                                type="number"
                                value={saleExpenses}
                                onChange={(e) => onSaleExpensesChange(e.target.value)}
                                sx={inputFieldSx(160)}
                            />
                        ),
                    },
                    {
                        label: t('SaleConsideration') || 'תמורת מכירה',
                        value: (
                            <TextField
                                size="small"
                                type="number"
                                value={salePriceInput}
                                onChange={(e) => onSalePriceInputChange(e.target.value)}
                                sx={inputFieldSx(160)}
                                placeholder={t('SellAmount')}
                            />
                        ),
                    }
                ]}
            />
        </Box>
    );
}

function CapitalGainIndexCalculationSection({
    direction,
    t,
    isRtl,
    cpi,
    cpiError,
    gains,
    onOpenReplacementPicker,
}) {
    const saleCurrBaseDesc = cpi?.saleCurrBaseDesc ?? cpi?.SaleCurrBaseDesc;
    const saleCurrBaseValue = cpi?.saleCurrBaseValue ?? cpi?.SaleCurrBaseValue;
    const showSaleCurrentBase =
        saleCurrBaseDesc &&
        saleCurrBaseValue != null &&
        saleCurrBaseDesc !== (cpi?.selectedBaseDesc ?? cpi?.SelectedBaseDesc);

    return (
        <Box>

            {cpiError && (
                <Typography color="error" sx={{ mb: 2, textAlign: isRtl ? 'right' : 'left' }}>
                    {cpiError}
                </Typography>
            )}
            {cpi && (
                <>
                    <InfoRowGroup
                        direction={direction}
                        items={[
                            {
                                label: t('SaleKnownIndex') || 'מדד מכירה ידוע',
                                value: beautifyNumber(cpi.saleKnownIndex ?? cpi.SaleKnownIndex, 3),
                            },
                            {
                                label: t('PurchaseKnownIndex') || 'מדד רכישה ידוע',
                                value: beautifyNumber(cpi.purchaseKnownIndex ?? cpi.PurchaseKnownIndex, 3),
                            },
                            {
                                label: t('SaleIndexPeriod'),
                                value: cpi.saleKnownIndexPeriod ?? cpi.SaleKnownIndexPeriod ?? '—',
                            },
                            {
                                label: t('PurchaseIndexPeriod'),
                                value: cpi.purchaseKnownIndexPeriod ?? cpi.PurchaseKnownIndexPeriod ?? '—',
                            },
                        ]}
                    />
                    <InfoRowGroup
                        direction={direction}
                        items={[
                            {
                                label: t('IndexIncreaseRate'),
                                value: `${beautifyNumber(cpi.indexIncreasePercent, 2)}%`,
                            },
                            {
                                label: t('SelectedIndexBase') || 'בסיס מדד שנבחר',
                                value: cpi.selectedBaseDesc ?? cpi.SelectedBaseDesc ?? '—',
                            },
                        ]}
                    />

                </>
            )}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: isRtl ? 'flex-end' : 'flex-start',
                    mt: 1.5,
                    direction,
                }}
            >
            <Button
                onClick={onOpenReplacementPicker}
                disabled={!gains}
                sx={{
                    textTransform: 'none',
                    bgcolor: '#304FFF',
                    border: '1px solid #304FFF',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '4px',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#253dcc', borderColor: '#253dcc', boxShadow: 'none' },
                    '&.Mui-disabled': { bgcolor: '#b0b8f5', borderColor: '#b0b8f5', color: '#fff' },
                }}
            >
                {t('ReplacementAssetsBalance') || 'שחלוף נכסים'}
                </Button>
            </Box>
        </Box>
    );
}

function CapitalGainTaxCalculationSection({
    direction,
    t,
    gains,
    tax,
    replacementSummary,
    adjustedGains,
    inflationaryTaxRate,
    onInflationaryTaxRateChange,
    realFrom2003TaxRate,
    onRealFrom2003TaxRateChange,
    realUntil2002TaxRate,
    onRealUntil2002TaxRateChange,
    advancePaid,
    setAdvancePaid,
    tableInputSx,
    isRtl,
    maxTaxConfig,
}) {
    return (
        <CapitalGainTaxTable
            direction={direction}
            t={t}
            gains={gains}
            tax={tax}
            replacementSummary={replacementSummary}
            adjustedGains={adjustedGains}
            inflationaryTaxRate={inflationaryTaxRate}
            onInflationaryTaxRateChange={onInflationaryTaxRateChange}
            realFrom2003TaxRate={realFrom2003TaxRate}
            onRealFrom2003TaxRateChange={onRealFrom2003TaxRateChange}
            realUntil2002TaxRate={realUntil2002TaxRate}
            onRealUntil2002TaxRateChange={onRealUntil2002TaxRateChange}
            advancePaid={advancePaid}
            setAdvancePaid={setAdvancePaid}
            tableInputSx={tableInputSx}
            isRtl={isRtl}
            maxTaxConfig={maxTaxConfig}
        />
    );
}


export {
    BoundsWrapper,
    EMPTY_REPLACEMENT_DATA,
    isAssetSold,
    getAssetIdValue,
    sanitizeFileNamePart,
    formatEnhancementAssetLabel,
    getEnhancementAssetId,
    SectionCard,
    EnhancementsPickerDialog,
    CapitalGainAssetDetailsSection,
    CapitalGainIndexCalculationSection,
    CapitalGainTaxCalculationSection,
};
